import { Component } from '@theme/component';
import {
  debounce,
  htmlEscape,
  localeUrl,
  pageUrl,
  stripTrademarkSymbols,
  isMobileBreakpoint,
} from '@theme/utilities';
import { EXTERNAL_HOSTS } from '@theme/api-endpoints';
import { queryAutocomplete } from '@theme/query-autocomplete';
import { RecentSearches } from '@theme/recent-searches';
import { morph } from '@theme/morph';

/**
 * Tailwind utility-class strings for the JS-rendered dropdown. The BEM class
 * names on each element are retained as JS selector hooks; these `tw:` classes
 * carry all styling (header-search.css was removed in favour of Tailwind).
 * Written as static string literals so the Tailwind build scans them.
 */
const CX = {
  label:
    'tw:m-0 tw:px-4 tw:pt-4 tw:pb-3 tw:text-sm tw:font-medium tw:leading-[18px] tw:tracking-[-0.14px]',
  rows: 'tw:list-none tw:m-0 tw:p-0',
  row: 'tw:flex tw:items-center tw:gap-3 tw:h-[50px] tw:px-4 tw:border-b tw:border-[#e3e4e5] tw:no-underline tw:cursor-pointer tw:last:border-b-0 tw:hover:bg-black/[0.04] tw:aria-selected:bg-black/[0.04]',
  link: 'tw:contents tw:text-inherit tw:no-underline',
  icon: 'tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:w-[21px] tw:h-[21px]',
  thumb: 'tw:shrink-0 tw:w-10 tw:h-10 tw:object-contain tw:rounded-md tw:bg-black/[0.04]',
  // ── Possible product matches: horizontal side-scrolling row (Figma) ──
  prodScroll:
    'tw:flex tw:gap-0 tw:list-none tw:m-0 tw:px-4 tw:pb-2 tw:overflow-x-auto tw:no-scrollbar',
  prodCard:
    'tw:flex tw:flex-col tw:items-center tw:shrink-0 tw:w-[91px] tw:cursor-pointer tw:transition-opacity tw:hover:opacity-70 tw:aria-selected:opacity-70',
  prodLink:
    'tw:flex tw:flex-col tw:items-center tw:gap-[11px] tw:w-full tw:no-underline tw:text-inherit',
  prodImg: 'tw:w-20 tw:h-20 tw:object-contain tw:rounded-[13px] tw:bg-black/[0.04]',
  prodName:
    'tw:w-full tw:text-center tw:text-xs tw:font-normal tw:leading-[14px] tw:text-black',
  text: 'tw:flex-1 tw:min-w-0 tw:text-sm tw:font-light tw:leading-[18px] tw:tracking-[-0.14px] tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap',
  textProduct: 'tw:font-semibold',
  textSuggestion: 'tw:font-light',
  mark: 'tw:bg-transparent tw:font-semibold',
  delete:
    'tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:w-[14px] tw:h-[14px] tw:ml-auto tw:p-0 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:max-lg:hidden',
  arrow: 'tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:w-[14px] tw:h-[14px] tw:ml-auto',
  arrowRecent: 'tw:lg:hidden',
  divider: 'tw:h-px tw:m-0 tw:border-0 tw:bg-[#e3e4e5]',
  status: 'tw:py-4 tw:px-2.5 tw:text-sm tw:text-center',
  statusError: 'tw:text-[#b3261e]',
  mBar: 'tw:fixed tw:top-0 tw:left-0 tw:right-0 tw:z-[100000] tw:flex tw:items-center tw:gap-2 tw:p-2.5 tw:bg-[rgba(246,246,246,0.92)] tw:backdrop-blur-[25px]',
  mWrap:
    'tw:flex-1 tw:flex tw:items-center tw:gap-2 tw:h-9 tw:px-3 tw:bg-white tw:border tw:border-[#dcdcdc] tw:rounded-[30px] tw:outline-none tw:focus-within:outline-none',
  mInput:
    'tw:flex-1 tw:min-w-0 tw:m-0 tw:p-0 tw:appearance-none tw:border-0 tw:outline-none tw:focus:outline-none tw:focus-visible:outline-none tw:shadow-none tw:focus:shadow-none tw:bg-transparent tw:text-xs tw:font-light tw:leading-4 tw:tracking-[-0.12px]',
  mSearchBtn:
    'tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:w-[27px] tw:h-[27px] tw:rounded-full tw:bg-[#f7f7f7] tw:border-0 tw:p-0 tw:cursor-pointer',
  mCancel:
    'tw:shrink-0 tw:p-0 tw:bg-transparent tw:border-0 tw:text-[10px] tw:font-light tw:leading-[13px] tw:tracking-[-0.1px] tw:underline tw:text-[#828282] tw:cursor-pointer',
};

/**
 * Stores whose products are sold on ShopGlobal rather than on this storefront.
 *
 * Two spellings because the two search services disagree: product-search reports
 * "ShopGlobal.com" and autocomplete reports "SHOP.COM Global" for the same
 * product. Matching both means this keeps working whichever way that is settled.
 */
const SHOPGLOBAL_STORE_NAMES = ['shopglobal.com', 'shop.com global'];

/** MA product type for affiliate products, which are sold through an interstitial. */
const AFFILIATE_PRODUCT_TYPE = 'CPA';

/**
 * Works out where a suggestion should go, in the order the search API intends:
 * a product on this Shopify store links to its own PDP, a product sold on
 * ShopGlobal to the off-site link the API supplies, and an affiliate product
 * through the interstitial that applies the shopper's attribution on the way.
 *
 * Mirrors the same decision on the search results card (ng-product-card.js) so a
 * product opens the same way wherever it was clicked, including ShopGlobal being
 * tested ahead of the affiliate type — those products carry both, and the results
 * card sends them off-site rather than through the interstitial. The two sources
 * word it differently (results read isGlobalProduct and prodId, suggestions read
 * storeName and productId) but resolve to the same destinations.
 *
 * Returns null when none of them apply, which is the signal not to render the
 * suggestion at all: a card with nowhere to go still looks clickable and does
 * nothing, which is the behaviour being fixed.
 *
 * @param {import('@theme/query-autocomplete').AutocompleteProduct} product
 * @returns {{ href: string, external: boolean } | null}
 */
function productLink(product) {
  const handle = product?.shopifyHandle || '';
  if (handle) {
    return { href: localeUrl(`/products/${encodeURIComponent(handle)}`), external: false };
  }

  const storeName = String(product?.storeName || '').trim().toLowerCase();
  if (SHOPGLOBAL_STORE_NAMES.includes(storeName)) {
    const shopGlobalPath = product?.shopGlobalLinkUrl || '';
    // Whatever path the service sends is followed as given, the same way the
    // results card follows productLinkUrl. Only the host is ours to add, so the
    // two surfaces build the same URL and a change to the path format needs no
    // change here.
    //
    // Resolved rather than fallen through, even when there is no path: these
    // products carry an affiliate type as well, and the interstitial below
    // cannot open them — the results card sends them off-site instead — so
    // continuing would only reach a destination that is wrong for them.
    return shopGlobalPath
      ? { href: `${EXTERNAL_HOSTS.shopGlobal}${shopGlobalPath}`, external: true }
      : null;
  }

  const productId = String(product?.productId || '').trim();
  if (product?.productType === AFFILIATE_PRODUCT_TYPE && productId) {
    // productId is the same identifier the results card passes as prodId. Any id
    // embedded in the off-site link above belongs to a different catalogue and is
    // not interchangeable with it.
    return {
      href: pageUrl('prod-interstitial', { prodID: productId }),
      external: true,
    };
  }

  return null;
}

/**
 * Header search type-ahead with recent searches, autocomplete suggestions,
 * and possible product matches.
 *
 * Focus (empty input): shows recent searches from localStorage.
 * Typing: queries the autocomplete API and renders three sections in order —
 * possible product matches (scrollable list, top), matching recents (max 2),
 * and suggestion term rows (below products). Products link to their PDP;
 * suggestion terms link to the search results page.
 *
 * Mobile (below the desktop breakpoint): full-screen overlay with a sticky
 * search bar and a cancel button. The mobile bar is rendered once inside the
 * results panel and preserved across content updates.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input   - The search text input.
 * @property {HTMLElement}      results - The results dropdown container.
 * @extends {Component<Refs>}
 */
class HeaderSearch extends Component {
  requiredRefs = ['input', 'results'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {(ReturnType<typeof debounce>) | null} */
  #mobileSearch = null;

  /** Current keyboard-highlighted row index (-1 = none). */
  #activeIndex = -1;

  /* ------------------------------------------------------------------ */
  /*  Lifecycle                                                          */
  /* ------------------------------------------------------------------ */

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    document.addEventListener('click', this.#handleDocumentClick, { signal });
    this.refs.results.addEventListener('click', this.#handleResultsClick);
    // Keep the page from creeping upward while typing — see #pinScroll.
    this.refs.input.addEventListener('input', this.#pinScroll, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
  }

  /**
   * Anchor the page scroll while typing.
   *
   * The search input lives in a `position: sticky` header. On each keystroke
   * the browser scrolls the page to keep the caret/input visible, but computes
   * against the input's in-flow (top-of-document) position, so the page creeps
   * upward on every key (and on delete). With the theme's global
   * `scroll-behavior: smooth` this shows as an animated drift.
   *
   * Fix: snapshot the scroll position on input and restore it for a few frames
   * with smooth behaviour disabled, so the browser's caret-keeping scroll is
   * neutralised without affecting the user's own scrolling.
   */
  #pinScroll = () => {
    const x = window.scrollX;
    const y = window.scrollY;
    const root = document.documentElement;
    const prevBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    let frame = 0;
    const tick = () => {
      if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
      if (++frame < 6) {
        requestAnimationFrame(tick);
      } else {
        root.style.scrollBehavior = prevBehavior;
      }
    };
    requestAnimationFrame(tick);
  };

  /* ------------------------------------------------------------------ */
  /*  Open / close / content update                                      */
  /* ------------------------------------------------------------------ */

  #open() {
    this.refs.results.hidden = false;
    this.refs.input.setAttribute('aria-expanded', 'true');
    this.setAttribute('open', '');
    this.#syncClear();
  }

  #close() {
    // A search still pending would re-open the panel after it was dismissed.
    this.#mobileSearch?.cancel();
    this.#activeFetch?.abort();
    this.refs.results.hidden = true;
    this.refs.results.innerHTML = '';
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.input.removeAttribute('aria-activedescendant');
    this.#activeIndex = -1;
    this.removeAttribute('open');
    this.#syncClear();
  }

  /**
   * Show the clear (✕) button only while the dropdown is open AND the input
   * has typed text — so it never appears on empty focus (recent searches) or
   * on the search results page where the query is pre-filled but closed.
   */
  #syncClear() {
    const clearBtn = this.refs.clear;
    if (!clearBtn) return;
    const show = this.hasAttribute('open') && this.refs.input.value.trim().length > 0;
    clearBtn.style.display = show ? 'flex' : 'none';
  }

  /**
   * Writes content HTML into the results panel.  On mobile the sticky
   * search bar is rendered once and preserved across subsequent updates.
   * On desktop the update is patched in via `morph` rather than a raw
   * `innerHTML` replace, so rows/thumbnails that are unchanged between two
   * consecutive states (e.g. Recent Searches → predictive results, or one
   * keystroke's results → the next) are left alone instead of being torn
   * down and rebuilt — that full-panel rebuild was what made the dropdown
   * visibly flicker on every keystroke.
   * @param {string} contentHtml
   */
  #updateContent(contentHtml) {
    if (isMobileBreakpoint()) {
      const mobileBar = this.#ensureMobileBar();
      while (mobileBar.nextSibling) mobileBar.nextSibling.remove();
      mobileBar.insertAdjacentHTML('afterend', contentHtml);
    } else {
      morph(this.refs.results, `<div>${contentHtml}</div>`);
    }
    this.#activeIndex = -1;
  }

  /**
   * Returns the mobile bar, building it only if it is not mounted yet. Building
   * it is an `innerHTML` write, which would detach its own input mid-keystroke.
   * @returns {Element}
   */
  #ensureMobileBar() {
    const existing = this.refs.results.querySelector('.header-search__mobile-bar');
    if (existing) return existing;

    this.refs.results.innerHTML = this.#mobileBarHtml(this.refs.input.value);
    this.#wireMobileBar();
    return /** @type {Element} */ (
      this.refs.results.querySelector('.header-search__mobile-bar')
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Event handlers                                                     */
  /* ------------------------------------------------------------------ */

  /** Close dropdown when a click lands outside this component. */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (!this.contains(event.target)) this.#close();
  };

  /** Delegated click handler for all interactive elements inside results. */
  #handleResultsClick = (event) => {
    // 1. Delete-recent button
    const deleteBtn = event.target.closest('[data-delete-recent]');
    if (deleteBtn) {
      event.preventDefault();
      event.stopPropagation();
      RecentSearches.removeTerm(deleteBtn.dataset.deleteRecent);
      const row = deleteBtn.closest('.header-search__row');
      if (row) row.remove();
      if (!this.refs.results.querySelector('.header-search__row')) {
        if (isMobileBreakpoint()) {
          // Keep overlay open on mobile even with no rows
          return;
        }
        this.#close();
      }
      return;
    }

    // 2. Product row (anchor → PDP) — save current query, let anchor navigate
    const productRow = event.target.closest('li[data-kind="product"]');
    if (productRow) {
      const query = this.#getCurrentQuery();
      if (query) RecentSearches.addTerm(query);
      return;
    }

    // 3. Suggestion row (anchor → SRP) — save the suggested term, let it navigate
    const suggestionRow = event.target.closest('li[data-kind="term"]');
    if (suggestionRow) {
      const term = suggestionRow.dataset.term;
      if (term) RecentSearches.addTerm(term);
      return;
    }

    // 4. Recent row — JS-driven navigate to the search results page
    const recentRow = event.target.closest('li[data-kind="recent"]');
    if (recentRow) {
      const term = recentRow.dataset.term;
      if (term) {
        this.refs.input.value = term;
        const mobileInput = this.refs.results.querySelector(
          '.header-search__mobile-input-wrap input'
        );
        if (mobileInput) mobileInput.value = term;
        this.#submit();
      }
      return;
    }
  };

  /** On focus with empty input show recent searches. */
  handleFocus = () => {
    const term = this.refs.input.value.trim();

    if (isMobileBreakpoint()) {
      // Mounted inside the tap, so the overlay's input already owns the caret by
      // the first keystroke rather than taking it mid-word on the first result.
      this.#ensureMobileBar();
      this.#open();
      if (term) {
        this.#search(term);
        return;
      }
    }

    if (!term) {
      this.#showRecentSearches();
    }
  };

  /** Debounced input handler — fires the autocomplete search. */
  handleInput = debounce(() => {
    const term = this.#getCurrentQuery();
    if (!term) {
      this.#showRecentSearches();
      return;
    }
    this.#search(term);
  }, 250);

  /**
   * Keyboard handler: arrows cycle rows, Enter submits or activates the
   * highlighted row, Escape closes the dropdown.
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    const rows = this.refs.results.querySelectorAll('[role="option"]');

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#navigateRows(rows, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#navigateRows(rows, -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const active = this.#activeIndex >= 0 ? rows[this.#activeIndex] : null;
      if (active) {
        const href = active.dataset.href;
        if (href) {
          // Product or suggestion — save the relevant term, then navigate.
          if (active.dataset.kind === 'term' && active.dataset.term) {
            RecentSearches.addTerm(active.dataset.term);
          } else if (active.dataset.kind === 'product') {
            const query = this.#getCurrentQuery();
            if (query) RecentSearches.addTerm(query);
          }
          window.location.href = href;
          return;
        }
        if (active.dataset.term) {
          this.refs.input.value = active.dataset.term;
          this.#submit();
          return;
        }
      }
      this.#submit();
    } else if (event.key === 'Escape') {
      this.#close();
      this.refs.input.blur();
    }
  };

  /** Search-button click handler. */
  submit = (event) => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    this.#submit();
  };

  /**
   * Clear-button click handler. Empties the input and reshows the empty-query
   * state (recent searches, or closes the dropdown when there are none). The
   * clear button is only visible while the dropdown is open (see search.liquid).
   */
  clear = (event) => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    this.refs.input.value = '';
    this.#activeFetch?.abort();
    this.refs.input.focus({ preventScroll: true });
    this.#showRecentSearches();
  };

  /* ------------------------------------------------------------------ */
  /*  Navigation & submission                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Cycle keyboard focus through option rows.
   * @param {NodeList} rows
   * @param {number} direction  1 (down) or -1 (up)
   */
  #navigateRows(rows, direction) {
    if (!rows.length) return;

    if (this.#activeIndex >= 0 && rows[this.#activeIndex]) {
      rows[this.#activeIndex].setAttribute('aria-selected', 'false');
    }

    this.#activeIndex += direction;
    if (this.#activeIndex >= rows.length) this.#activeIndex = 0;
    if (this.#activeIndex < 0) this.#activeIndex = rows.length - 1;

    const active = rows[this.#activeIndex];
    active.setAttribute('aria-selected', 'true');
    active.scrollIntoView({ block: 'nearest' });

    if (active.id) {
      this.refs.input.setAttribute('aria-activedescendant', active.id);
    }
  }

  /** Navigate to the search results page, saving the term first. */
  #submit() {
    const term = this.#getCurrentQuery();
    if (!term) {
      this.refs.input.focus({ preventScroll: true });
      return;
    }
    RecentSearches.addTerm(term);
    const searchUrl = this.dataset.searchUrl || Theme?.routes?.search_url || '/search';
    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', stripTrademarkSymbols(term) || term);
    window.location.href = url.toString();
  }

  /**
   * Returns the current query from whichever input is active.
   * @returns {string}
   */
  #getCurrentQuery() {
    if (isMobileBreakpoint()) {
      const mobileInput = this.refs.results.querySelector(
        '.header-search__mobile-input-wrap input'
      );
      // The header input only catches up when the debounce fires, so it is only
      // the fresher of the two while it is the one being typed in.
      if (mobileInput && document.activeElement !== this.refs.input) {
        return mobileInput.value.trim();
      }
    }
    return this.refs.input.value.trim();
  }

  /* ------------------------------------------------------------------ */
  /*  Recent searches                                                    */
  /* ------------------------------------------------------------------ */

  /** Show the recent-searches list (empty-query state). */
  #showRecentSearches() {
    const recents = RecentSearches.getTerms(5);

    if (!recents.length) {
      if (isMobileBreakpoint()) {
        // On mobile always open the overlay so the user gets the mobile bar
        this.#updateContent('');
        this.#open();
      } else {
        this.#close();
      }
      return;
    }

    const label = Theme?.translations?.search_recent_searches || 'Recent Searches';
    let idx = 0;
    const rows = recents
      .map((term) => {
        const html = this.#recentRowHtml(term, idx);
        idx++;
        return html;
      })
      .join('');

    this.#updateContent(
      `<div class="header-search__category-label ${CX.label}">${htmlEscape(label)}</div>` +
        `<ul class="header-search__rows ${CX.rows}">${rows}</ul>`
    );
    this.#open();
  }

  /* ------------------------------------------------------------------ */
  /*  Autocomplete search                                                */
  /* ------------------------------------------------------------------ */

  /**
   * Queries the autocomplete API (Elasticsearch) and renders the combined
   * result. Elasticsearch returns both term suggestions and possible product
   * matches (including MA SKU / Product ID lookups) in a single call.
   *
   * Deliberately does not render an interim "Searching…" state: whatever is
   * currently shown (Recent Searches, or the previous keystroke's results)
   * stays mounted until the new result is ready, then the panel is updated
   * once via `#updateContent`/morph. Swapping to a loading state and back on
   * every keystroke was the dropdown's main source of visible flicker.
   * @param {string} term
   */
  async #search(term) {
    const controller = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = controller;

    /** @type {import('@theme/query-autocomplete').AutocompleteResult | null} */
    let acResult = null;
    try {
      acResult = await queryAutocomplete({
        query: term,
        termsSize: 10,
        productsSize: 10,
        signal: controller.signal,
      });
    } catch (error) {
      // Re-thrown AbortError means a newer query superseded this one.
      if (error?.name === 'AbortError') return;
      acResult = null;
    }

    if (controller.signal.aborted) return;

    const html = this.#renderCombinedResult(term, acResult);
    if (html) {
      this.#updateContent(html);
      this.#open();
    } else if (isMobileBreakpoint()) {
      // Zero matches on mobile: keep the full-screen overlay (and its sticky
      // search bar) mounted with an empty body, so the shopper can keep
      // typing or submit to the SRP. #close() would tear the overlay down
      // mid-typing — see the empty-state branch in #renderCombinedResult
      // (MA-1888 + PR #1168).
      this.#updateContent('');
      this.#open();
    } else {
      // Desktop: nothing to show, so close the dropdown quietly rather than
      // leaving an empty panel floating under the input.
      this.#close();
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Combined result renderer                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Composes the multi-section dropdown from the autocomplete result.
   * Order: possible product matches (top) → matching recents (max 2) →
   * suggestion terms (below products).
   *
   * @param {string} term
   * @param {import('@theme/query-autocomplete').AutocompleteResult | null} acResult
   * @returns {string}
   */
  #renderCombinedResult(term, acResult) {
    const acOk = acResult?.ok === true;
    // Each suggestion is paired with where it goes, and any that resolves nowhere
    // is dropped. Those used to render as a full card — image, name, pointer
    // cursor, hover state — over a link that fell back to "#", so they looked
    // interactive and did nothing.
    //
    // Dropped here rather than in the autocomplete client so the analytics
    // productsCount keeps reporting what the service actually returned. The
    // difference between that count and what renders is the measure of how many
    // suggestions cannot be opened, and filtering upstream would hide it.
    //
    // Resolved before the slice so a full row of ten is still shown whenever that
    // many usable suggestions come back.
    const products = acOk
      ? (acResult.data.products || [])
          .map((product) => ({ product, link: productLink(product) }))
          .filter((entry) => entry.link)
          .slice(0, 10)
      : [];
    const terms = acOk ? (acResult.data.terms || []).slice(0, 10) : [];

    const sections = [];
    let rowIdx = 0;

    // ── 1. Possible product matches (top) — scrollable list ──────────
    if (products.length) {
      const label =
        Theme?.translations?.search_possible_matches || 'Possible Product Matches';
      const rows = products
        .map((entry) => this.#productRowHtml(entry.product, entry.link, rowIdx++))
        .join('');
      sections.push(
        `<div class="header-search__category-label ${CX.label}">${htmlEscape(label)}</div>` +
          `<ul class="header-search__rows--products ${CX.prodScroll}">${rows}</ul>`
      );
    }

    // ── 2. Matching recent searches (max 2) — above suggestions ──────
    const lowerTerm = term.toLowerCase();
    const matchingRecents = RecentSearches.getTerms(10)
      .filter(
        (t) => t.toLowerCase().includes(lowerTerm) && t.toLowerCase() !== lowerTerm
      )
      .slice(0, 2);

    if (matchingRecents.length) {
      const label = Theme?.translations?.search_recent_searches || 'Recent Searches';
      const rows = matchingRecents
        .map((t) => this.#recentRowHtml(t, rowIdx++))
        .join('');
      sections.push(
        `<div class="header-search__category-label ${CX.label}">${htmlEscape(label)}</div>` +
          `<ul class="header-search__rows ${CX.rows}">${rows}</ul>`
      );
    }

    // ── 3. Suggestion term rows (below products) ─────────────────────
    // Categories are not returned by the API, so no client-side filtering
    // is required.
    if (terms.length) {
      const label = Theme?.translations?.search_suggestions || 'Suggestions';
      const rows = terms
        .map((item) => this.#suggestionRowHtml(item, term, rowIdx++))
        .join('');
      sections.push(
        `<div class="header-search__category-label ${CX.label}">${htmlEscape(label)}</div>` +
          `<ul class="header-search__rows ${CX.rows}">${rows}</ul>`
      );
    }

    // ── Empty / error state ──────────────────────────────────────────
    if (!sections.length) {
      if (!acOk) {
        const errorMsg =
          Theme?.translations?.search_service_error ||
          'Search is temporarily unavailable. Please try again.';
        return `<div class="header-search__status header-search__status--error ${CX.status} ${CX.statusError}">${htmlEscape(errorMsg)}</div>`;
      }
      // Successful query with zero autocomplete matches. Render NOTHING here:
      // shoppers read "No results found" as "this store has nothing" and stop
      // before pressing Enter, even though the full SRP often does match
      // (MA-1888).
      //
      // Do NOT reintroduce a message to keep the surface alive — that was
      // PR #1168's fix and it re-broke MA-1888. The real constraint is in
      // #search(): an empty string used to fall through to #close(), which
      // hides `refs.results` — and on mobile that element IS the full-screen
      // overlay (search.liquid: `tw:max-lg:fixed tw:max-lg:inset-0`), so the
      // whole search UI vanished mid-typing. #search() now handles the empty
      // case explicitly instead, so both constraints hold at once:
      //   1. never render "No results found"            (MA-1888)
      //   2. never close the mobile overlay mid-typing  (PR #1168)
      // Changing either one alone will re-break the other.
      return '';
    }

    // No "View all results" link — not part of the approved Figma design.
    // Sections flow contiguously; the per-row bottom borders act as dividers
    // (the Figma has no separate section divider lines).
    return sections.join('');
  }

  /* ------------------------------------------------------------------ */
  /*  Row HTML builders                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Recent-search row.
   * Desktop: clock icon, text, delete button.
   * Mobile:  clock icon, text, arrow (delete hidden via CSS).
   *
   * @param {string} term
   * @param {number} index
   * @returns {string}
   */
  #recentRowHtml(term, index) {
    const id = `search-opt-${index}`;
    const iconRecent = this.dataset.iconRecent || '';
    const iconClose = this.dataset.iconClose || '';
    const iconArrow = this.dataset.iconArrow || '';
    const deleteLabel =
      Theme?.translations?.search_delete_recent || 'Remove search term';

    return (
      `<li class="header-search__row ${CX.row}" role="option" id="${id}"` +
      ` data-kind="recent" data-term="${htmlEscape(term)}" aria-selected="false">` +
      `<span class="header-search__row-icon ${CX.icon}">${iconRecent}</span>` +
      `<span class="header-search__row-text ${CX.text}">${htmlEscape(term)}</span>` +
      `<button class="header-search__row-action header-search__row-action--delete ${CX.delete} fs-predictive-search__recent-delete"` +
      ` data-delete-recent="${htmlEscape(term)}"` +
      ` aria-label="${htmlEscape(deleteLabel)}" type="button">${iconClose}</button>` +
      `<span class="header-search__row-arrow header-search__row-arrow--recent ${CX.arrow} ${CX.arrowRecent}">${iconArrow}</span>` +
      `</li>`
    );
  }

  /**
   * Suggestion term row with query-match highlighting. Rendered as an anchor
   * to the search results page (the API provides a relative `url`, e.g.
   * "/search?q=vitamins"); clicking performs the keyword search.
   *
   * @param {{ term: string, url?: string } | string} item
   * @param {string} query
   * @param {number} index
   * @returns {string}
   */
  #suggestionRowHtml(item, query, index) {
    const term = typeof item === 'string' ? item : item.term || '';
    if (!term) return '';

    const id = `search-opt-${index}`;
    const iconSearch = this.dataset.iconSearch || '';
    const iconArrow = this.dataset.iconArrow || '';
    const highlighted = this.#highlightTerm(term, query);
    const href = this.#searchUrlFor(term, typeof item === 'object' ? item.url : '');

    return (
      `<li class="header-search__row ${CX.row}" role="option" id="${id}"` +
      ` data-kind="term" data-term="${htmlEscape(term)}" data-href="${htmlEscape(href)}"` +
      ` aria-selected="false">` +
      `<a class="header-search__row-link ${CX.link} fs-predictive-search__term-link" href="${htmlEscape(href)}">` +
      `<span class="header-search__row-icon ${CX.icon}">${iconSearch}</span>` +
      `<span class="header-search__row-text header-search__row-text--suggestion ${CX.text} ${CX.textSuggestion}">${highlighted}</span>` +
      `<span class="header-search__row-arrow ${CX.arrow}">${iconArrow}</span>` +
      `</a>` +
      `</li>`
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Product matches (scrollable list)                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Single possible-product-match card — square image on top, name below,
   * rendered inside the horizontal side-scrolling row (Figma). Clicking the image
   * or the name follows the link the caller resolved, which is this store's PDP
   * for a product that has one and an off-site destination otherwise.
   *
   * @param {import('@theme/query-autocomplete').AutocompleteProduct} product
   * @param {{ href: string, external: boolean }} link - Resolved by productLink().
   * @param {number} index
   * @returns {string}
   */
  #productRowHtml(product, link, index) {
    const id = `search-opt-${index}`;
    // `productName` arrives already entity-decoded from the autocomplete client.
    const name =
      product?.productName || Theme?.translations?.search_product_fallback || 'Product';
    const thumb = product?.thumbnailUrl || '';
    const href = link.href;
    // ShopGlobal and affiliate products leave this storefront, so they open in a
    // new tab and keep the search behind them — the same treatment they get on
    // the results card. Products on this store navigate in place.
    const target = link.external ? ' target="_blank" rel="noopener"' : '';

    const thumbHtml = thumb
      ? `<img src="${htmlEscape(thumb)}" alt="" class="header-search__prod-img ${CX.prodImg}" loading="lazy" width="80" height="80">`
      : `<span class="header-search__prod-img ${CX.prodImg}"></span>`;

    return (
      `<li class="header-search__prod-card ${CX.prodCard}" role="option" id="${id}"` +
      ` data-kind="product" data-href="${htmlEscape(href)}"` +
      ` data-product-id="${htmlEscape(product?.productId || '')}" aria-selected="false">` +
      `<a class="header-search__prod-link ${CX.prodLink} fs-predictive-search__product-link"` +
      ` href="${htmlEscape(href)}"${target}>` +
      thumbHtml +
      `<span class="header-search__prod-name ${CX.prodName}">${htmlEscape(name)}</span>` +
      `</a>` +
      `</li>`
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Mobile bar                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Renders the sticky mobile search bar HTML.
   * @param {string} query  Current input value to pre-fill.
   * @returns {string}
   */
  #mobileBarHtml(query) {
    const iconSearch = this.dataset.iconSearch || '';
    const cancelText = Theme?.translations?.search_cancel || 'Cancel';
    const searchLabel = Theme?.translations?.search_label || 'Search';
    const placeholder = this.refs.input.placeholder || '';

    return (
      `<div class="header-search__mobile-bar ${CX.mBar}">` +
      `<div class="header-search__mobile-input-wrap ${CX.mWrap}">` +
      `<input type="search" value="${htmlEscape(query)}"` +
      ` placeholder="${htmlEscape(placeholder)}"` +
      ` autocomplete="off" spellcheck="false" class="${CX.mInput} fs-search__input">` +
      `<button class="header-search__mobile-search-btn ${CX.mSearchBtn} fs-search__submit" type="button" aria-label="${htmlEscape(searchLabel)}">${iconSearch}</button>` +
      `</div>` +
      `<button class="header-search__cancel-btn ${CX.mCancel} fs-predictive-search__cancel" type="button">${htmlEscape(cancelText)}</button>` +
      `</div>`
    );
  }

  /** Wires event listeners on the dynamically-rendered mobile bar. */
  #wireMobileBar() {
    const mobileInput = this.refs.results.querySelector(
      '.header-search__mobile-input-wrap input'
    );
    const cancelBtn = this.refs.results.querySelector('.header-search__cancel-btn');
    const searchBtn = this.refs.results.querySelector('.header-search__mobile-search-btn');

    if (mobileInput) {
      this.#mobileSearch = debounce(() => {
        this.refs.input.value = mobileInput.value;
        const term = mobileInput.value.trim();
        if (!term) {
          this.#showRecentSearches();
          return;
        }
        this.#search(term);
      }, 250);

      mobileInput.addEventListener('input', this.#mobileSearch);

      mobileInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.refs.input.value = mobileInput.value;
          this.#submit();
        } else if (event.key === 'Escape') {
          this.#close();
        }
      });

      requestAnimationFrame(() => {
        mobileInput.focus({ preventScroll: true });
        // Focusing text that came from the `value` attribute leaves the caret at 0.
        const end = mobileInput.value.length;
        mobileInput.setSelectionRange(end, end);
      });
    }

    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        // Clear both the drawer input and the persistent header input, then
        // close — #close() cancels the pending search that would otherwise
        // re-populate the header input ~250 ms later.
        if (mobileInput) mobileInput.value = '';
        this.refs.input.value = '';
        this.#close();
      });
    }

    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        if (mobileInput) this.refs.input.value = mobileInput.value;
        this.#submit();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Utilities                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Wraps the query-matching substring in `<mark>` for visual emphasis.
   * @param {string} text
   * @param {string} query
   * @returns {string}  Safe HTML with `<mark>` around the match.
   */
  #highlightTerm(text, query) {
    if (!query) return htmlEscape(text);

    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return htmlEscape(text);

    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + query.length);
    const after = text.slice(idx + query.length);

    return `${htmlEscape(before)}<mark class="${CX.mark}">${htmlEscape(match)}</mark>${htmlEscape(after)}`;
  }

  /**
   * Builds a search-results-page URL for a term. The path comes from the
   * relative `url` returned by the API (falling back to the theme's search
   * route); the query string is always rebuilt here.
   *
   * The API does not URL-encode the term it embeds, so a suggestion carrying a
   * trademark entity arrives as `/search?q=isotonix&reg;` — the `&reg;` is then
   * parsed as a second, empty parameter. Discarding the API query string and
   * setting `q` ourselves avoids that.
   *
   * @param {string} term
   * @param {string} [apiUrl]  Relative URL from the API (e.g. "/search?q=vita").
   * @returns {string}
   */
  #searchUrlFor(term, apiUrl) {
    // Rows display the mark (`Isotonix®`); the search query must not carry it.
    const queryTerm = stripTrademarkSymbols(term) || term;
    const searchUrl = this.dataset.searchUrl || Theme?.routes?.search_url || '/search';
    // Only a same-origin relative path is trusted from the API.
    const base = apiUrl && apiUrl.startsWith('/') ? apiUrl : searchUrl;
    let url;
    try {
      url = new URL(base, location.origin);
    } catch {
      url = new URL(searchUrl, location.origin);
    }
    url.search = '';
    url.searchParams.set('q', queryTerm);
    return url.pathname + url.search;
  }
}

if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch);
}
